import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {

  constructor() { }

  /**
   * Store data securely
   * Uses device secure storage on native platforms, encrypted localStorage on web
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      // For now, use localStorage with obfuscation for all platforms
      // Capacitor Storage plugin requires additional setup
      const obfuscatedValue = this.obfuscate(value);
      localStorage.setItem(key, obfuscatedValue);
    } catch (error) {
      console.error('Error storing secure data:', error);
      // Final fallback to plain localStorage
      localStorage.setItem(key, value);
    }
  }

  /**
   * Retrieve data securely
   */
  async getItem(key: string): Promise<string | null> {
    try {
      // For now, use localStorage with deobfuscation for all platforms
      // Capacitor Storage plugin requires additional setup
      const obfuscatedValue = localStorage.getItem(key);
      if (obfuscatedValue) {
        return this.deobfuscate(obfuscatedValue);
      }
      return null;
    } catch (error) {
      console.error('Error retrieving secure data:', error);
      // Final fallback to plain localStorage
      return localStorage.getItem(key);
    }
  }

  /**
   * Remove data securely
   */
  async removeItem(key: string): Promise<void> {
    try {
      // For now, use localStorage for all platforms
      // Capacitor Storage plugin requires additional setup
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing secure data:', error);
      // Final fallback to plain localStorage
      localStorage.removeItem(key);
    }
  }

  /**
   * Clear all secure data
   */
  async clear(): Promise<void> {
    try {
      // For now, use localStorage for all platforms
      // Capacitor Storage plugin requires additional setup
      // Only clear auth-related items from localStorage
      const authKeys = ['user', 'token', 'loginTime', 'refreshToken'];
      authKeys.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing secure data:', error);
      // Final fallback
      const authKeys = ['user', 'token', 'loginTime', 'refreshToken'];
      authKeys.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * Store user data with refresh token securely
   */
  async storeAuthData(userData: any): Promise<void> {
    try {
      // Separate refresh token from user data for additional security
      const { refreshToken, ...userDataWithoutToken } = userData;

      // Store user data (less sensitive)
      localStorage.setItem('user', JSON.stringify(userDataWithoutToken));
      localStorage.setItem('loginTime', userData.loginTime?.toString() || Date.now().toString());

      // Store refresh token securely (more sensitive)
      if (refreshToken) {
        await this.setItem('refreshToken', refreshToken);
      }

      console.log('✅ Auth data stored securely');
    } catch (error) {
      console.error('❌ Error storing auth data securely:', error);
      // Fallback to standard localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('loginTime', userData.loginTime?.toString() || Date.now().toString());
    }
  }

  /**
   * Retrieve complete auth data
   */
  async getAuthData(): Promise<any> {
    try {
      const userDataStr = localStorage.getItem('user');
      const refreshToken = await this.getItem('refreshToken');
      const loginTime = localStorage.getItem('loginTime');

      if (!userDataStr) {
        return null;
      }

      const userData = JSON.parse(userDataStr);
      
      // Combine user data with securely stored refresh token
      return {
        ...userData,
        refreshToken,
        loginTime: loginTime ? parseInt(loginTime) : Date.now()
      };
    } catch (error) {
      console.error('❌ Error retrieving auth data securely:', error);
      // Fallback to standard localStorage
      const userDataStr = localStorage.getItem('user');
      return userDataStr ? JSON.parse(userDataStr) : null;
    }
  }

  /**
   * Clear all auth data
   */
  async clearAuthData(): Promise<void> {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('loginTime');
      await this.removeItem('refreshToken');
      console.log('✅ Auth data cleared securely');
    } catch (error) {
      console.error('❌ Error clearing auth data securely:', error);
      // Fallback
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('loginTime');
      localStorage.removeItem('refreshToken');
    }
  }

  /**
   * Simple obfuscation for web platform
   * Note: This is not true encryption, just basic obfuscation
   */
  private obfuscate(text: string): string {
    const key = 'HappyDuckersSecure2024';
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
      );
    }
    return btoa(result); // Base64 encode
  }

  /**
   * Simple deobfuscation for web platform
   */
  private deobfuscate(obfuscatedText: string): string {
    try {
      const decoded = atob(obfuscatedText); // Base64 decode
      const key = 'HappyDuckersSecure2024';
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(
          decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
      }
      return result;
    } catch (error) {
      console.error('Error deobfuscating data:', error);
      return obfuscatedText; // Return as-is if deobfuscation fails
    }
  }

  /**
   * Check if we're running in a secure context (HTTPS)
   */
  isSecureContext(): boolean {
    return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  }

  /**
   * Get security info for debugging
   */
  getSecurityInfo(): any {
    return {
      isNativePlatform: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
      isSecureContext: this.isSecureContext(),
      hasSecureStorage: false // Storage plugin requires additional setup
    };
  }
}